import React, { useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import PlaceIcon from "@mui/icons-material/Place";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { Theme } from "@mui/material/styles";
import type { ZoneCoordinate } from "common/types/permits";

const MAPBOX_STYLE = "mapbox://styles/ashtrix/clkuc7ds6000901po6pw98iuj";
const DEFAULT_ZOOM = 12;
const SINGLE_ZONE_ZOOM = 14;
const FIT_BOUNDS_MAX_ZOOM = 15;
const FIT_BOUNDS_PADDING = 50;

export interface ZoneMapDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  zones: ZoneCoordinate[];
  loading?: boolean;
}

export const ZoneMapDialog: React.FC<ZoneMapDialogProps> = ({ open, onClose, title, zones, loading = false }) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  const mapboxApiKey = process.env.REACT_APP_MAPBOX_API_KEY;

  const calculateCenter = useCallback((): [number, number] => {
    if (zones.length === 0) {
      return [0, 0];
    }
    // ZoneCoordinate.center is [lat, lng], Mapbox uses [lng, lat]
    const avgLat = zones.reduce((sum, z) => sum + z.center[0], 0) / zones.length;
    const avgLng = zones.reduce((sum, z) => sum + z.center[1], 0) / zones.length;
    return [avgLng, avgLat]; // Mapbox format
  }, [zones]);

  const cleanupMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
  }, []);

  const cleanupMap = useCallback(() => {
    cleanupMarkers();
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, [cleanupMarkers]);

  const flyToZone = useCallback((zone: ZoneCoordinate) => {
    if (!mapRef.current) {
      return;
    }
    // ZoneCoordinate.center is [lat, lng], Mapbox uses [lng, lat]
    const [lat, lng] = zone.center;
    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: SINGLE_ZONE_ZOOM,
      essential: true,
    });
  }, []);

  const addMarkers = useCallback(() => {
    if (!mapRef.current || zones.length === 0) {
      return;
    }

    zones.forEach((zone) => {
      const markerEl = document.createElement("div");
      ReactDOM.render(
        <PlaceIcon
          sx={{
            fontSize: 32,
            color: zone.color || "primary.main",
            cursor: "pointer",
            filter: "drop-shadow(0px 2px 2px rgba(0,0,0,0.3))",
          }}
        />,
        markerEl,
      );

      // ZoneCoordinate.center is [lat, lng], Mapbox uses [lng, lat]
      const [lat, lng] = zone.center;

      const popup = new mapboxgl.Popup({
        offset: [0, -32],
        closeButton: false,
      }).setText(zone.name);

      const marker = new mapboxgl.Marker(markerEl).setLngLat([lng, lat]).setPopup(popup);

      markerEl.addEventListener("mouseenter", () => marker.togglePopup());
      markerEl.addEventListener("mouseleave", () => marker.togglePopup());

      marker.addTo(mapRef.current as mapboxgl.Map);
      markersRef.current.push(marker);
    });
  }, [zones]);

  const fitBoundsToZones = useCallback(() => {
    if (!mapRef.current || zones.length === 0) {
      return;
    }

    if (zones.length === 1) {
      // For single zone, center on that zone
      const [lat, lng] = zones[0].center;
      mapRef.current.setCenter([lng, lat]);
      mapRef.current.setZoom(SINGLE_ZONE_ZOOM);
    } else {
      // For multiple zones, fit bounds to show all
      const bounds = new mapboxgl.LngLatBounds();
      zones.forEach((zone) => {
        const [lat, lng] = zone.center;
        bounds.extend([lng, lat]);
      });
      mapRef.current.fitBounds(bounds, {
        padding: FIT_BOUNDS_PADDING,
        maxZoom: FIT_BOUNDS_MAX_ZOOM,
      });
    }
  }, [zones]);

  const initializeMap = useCallback(() => {
    if (!mapContainerRef.current || !mapboxApiKey || mapRef.current) {
      return;
    }

    mapboxgl.accessToken = mapboxApiKey;

    const center = calculateCenter();

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center,
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
    });

    // Disable map rotation
    map.dragRotate.disable();

    map.on("error", (e) => {
      console.error("Mapbox map error:", e);
    });

    map.once("load", () => {
      addMarkers();
      fitBoundsToZones();
    });

    mapRef.current = map;
  }, [mapboxApiKey, calculateCenter, addMarkers, fitBoundsToZones]);

  useEffect(() => {
    if (open && !loading && zones.length > 0) {
      // Small delay to ensure the dialog container is rendered
      const timeoutId = setTimeout(() => {
        initializeMap();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [open, loading, zones.length, initializeMap]);

  useEffect(() => {
    if (!open) {
      cleanupMap();
    }
  }, [open, cleanupMap]);

  useEffect(() => {
    return () => {
      cleanupMap();
    };
  }, [cleanupMap]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (!mapboxApiKey) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography color="error">Map configuration error: API key not found.</Typography>
        </Box>
      );
    }

    if (zones.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography color="text.secondary">No zones configured for this permit type.</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: "100%",
          width: "100%",
        }}
      >
        {/* Map Container */}
        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "2 1 0" },
            minHeight: { xs: 300, md: "auto" },
            position: "relative",
            ".mapboxgl-popup": {
              zIndex: 15,
            },
            ".mapboxgl-popup-content": {
              padding: "4px 10px !important",
              backgroundColor: "#333333 !important",
              color: "#ffffff !important",
              borderRadius: "4px !important",
            },
            ".mapboxgl-popup-tip": {
              borderTopColor: "#333333 !important",
            },
            ".mapboxgl-ctrl-logo": {
              display: "none !important",
            },
          }}
        >
          <Box
            ref={mapContainerRef}
            sx={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        </Box>

        {/* Zone List */}
        <Box
          sx={{
            flex: { xs: "0 0 auto", md: "1 1 0" },
            maxHeight: { xs: 200, md: "100%" },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderLeft: { xs: "none", md: 1 },
            borderTop: { xs: 1, md: "none" },
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, pt: 2, pb: 1, fontWeight: 600, flexShrink: 0 }}>
            Zones ({zones.length})
          </Typography>
          <Box sx={{ flex: 1, overflow: "auto" }}>
            <List dense disablePadding>
              {zones.map((zone, index) => (
                <ListItem key={`${zone.name}-${index}`} disablePadding>
                  <ListItemButton onClick={() => flyToZone(zone)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PlaceIcon sx={{ color: zone.color || "primary.main" }} />
                    </ListItemIcon>
                    <ListItemText primary={zone.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "90vw",
          maxWidth: { lg: "1200px", xs: "100%" },
          height: { xs: "80vh", sm: "600px" },
          borderRadius: (theme: Theme) => theme.spacing(1.5),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden" }}>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default ZoneMapDialog;
